import React, { useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';

// ─── Toolbar button ────────────────────────────────────────────────────────────

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  title: string;
  disabled?: boolean;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  active,
  title,
  disabled,
  children,
}) => (
  <button
    type="button"
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
    disabled={disabled}
    title={title}
    aria-label={title}
    className="p-1.5 rounded transition-colors"
    style={{
      background: active ? 'var(--crimson-dim)' : 'transparent',
      color: active ? 'var(--crimson-light)' : 'var(--text-muted)',
      border: active ? '1px solid var(--crimson-border)' : '1px solid transparent',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
    }}
    onMouseOver={(e) => {
      if (active || disabled) return;
      const el = e.currentTarget as HTMLElement;
      el.style.background = 'var(--bg-high)';
      el.style.color = 'var(--text)';
    }}
    onMouseOut={(e) => {
      if (active || disabled) return;
      const el = e.currentTarget as HTMLElement;
      el.style.background = 'transparent';
      el.style.color = 'var(--text-muted)';
    }}
  >
    {children}
  </button>
);

const Divider: React.FC = () => (
  <div className="w-px h-4 mx-0.5" style={{ background: 'var(--border-mid)' }} />
);

// ─── Toolbar ──────────────────────────────────────────────────────────────────

const Toolbar: React.FC<{ editor: Editor }> = ({ editor }) => (
  <div
    className="flex items-center gap-0.5 px-3 py-2 flex-wrap sticky top-0 z-10"
    style={{
      background: 'var(--bg-surface, var(--bg-mid))',
      borderBottom: '1px solid var(--border)',
    }}
  >
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleBold().run()}
      active={editor.isActive('bold')}
      title="Bold"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
        <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
        <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
      </svg>
    </ToolbarButton>
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleItalic().run()}
      active={editor.isActive('italic')}
      title="Italic"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <line x1="19" y1="4" x2="10" y2="4" />
        <line x1="14" y1="20" x2="5" y2="20" />
        <line x1="15" y1="4" x2="9" y2="20" />
      </svg>
    </ToolbarButton>

    <Divider />

    <ToolbarButton
      onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      active={editor.isActive('heading', { level: 1 })}
      title="Heading 1"
    >
      <span className="text-xs font-bold px-0.5">H1</span>
    </ToolbarButton>
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      active={editor.isActive('heading', { level: 2 })}
      title="Heading 2"
    >
      <span className="text-xs font-bold px-0.5">H2</span>
    </ToolbarButton>
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      active={editor.isActive('heading', { level: 3 })}
      title="Heading 3"
    >
      <span className="text-xs font-bold px-0.5">H3</span>
    </ToolbarButton>

    <Divider />

    <ToolbarButton
      onClick={() => editor.chain().focus().toggleBulletList().run()}
      active={editor.isActive('bulletList')}
      title="Bullet list"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <line x1="9" y1="6" x2="20" y2="6" />
        <line x1="9" y1="12" x2="20" y2="12" />
        <line x1="9" y1="18" x2="20" y2="18" />
        <circle cx="4" cy="6" r="1.5" fill="currentColor" stroke="none" />
        <circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none" />
        <circle cx="4" cy="18" r="1.5" fill="currentColor" stroke="none" />
      </svg>
    </ToolbarButton>
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleOrderedList().run()}
      active={editor.isActive('orderedList')}
      title="Ordered list"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <line x1="10" y1="6" x2="21" y2="6" />
        <line x1="10" y1="12" x2="21" y2="12" />
        <line x1="10" y1="18" x2="21" y2="18" />
        <path d="M4 6h1v4" />
        <path d="M4 10h2" />
        <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
      </svg>
    </ToolbarButton>

    <Divider />

    <ToolbarButton
      onClick={() => editor.chain().focus().toggleBlockquote().run()}
      active={editor.isActive('blockquote')}
      title="Blockquote"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
        <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" />
      </svg>
    </ToolbarButton>
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      active={editor.isActive('codeBlock')}
      title="Code block"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    </ToolbarButton>
  </div>
);

// ─── NoteEditor ───────────────────────────────────────────────────────────────

interface NoteEditorProps {
  content: string;
  onSave: (content: string) => void;
  editable?: boolean;
  noteId: string; // used to reset editor when switching notes
}

const NoteEditor: React.FC<NoteEditorProps> = ({ content, onSave, editable = true, noteId }) => {
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushSave = useCallback((nextContent: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSaveRef.current(nextContent);
    }, 1000);
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Placeholder.configure({ placeholder: 'Start writing...' }),
      Typography,
    ],
    content: (() => {
      try {
        return content ? (JSON.parse(content) as object) : '<p></p>';
      } catch {
        return content || '<p></p>';
      }
    })(),
    editable,
    onUpdate: ({ editor: e }) => {
      flushSave(JSON.stringify(e.getJSON()));
    },
    editorProps: {
      attributes: {
        // note-editor-content must have font-size >= 16px to prevent iOS Safari zoom
        class: 'prose prose-invert max-w-none min-h-[40vh] px-6 py-5 outline-none note-editor-content',
      },
    },
  }, [noteId]); // recreate editor when note changes

  // Sync external content updates (e.g. switching notes)
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const current = JSON.stringify(editor.getJSON());
    if (current === content) return;
    try {
      const parsed = content ? JSON.parse(content) : '<p></p>';
      editor.commands.setContent(parsed as object, false);
    } catch {
      editor.commands.setContent(content || '<p></p>', false);
    }
  }, [content, editor]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(editable);
  }, [editable, editor]);

  // Flush any pending debounce on unmount
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  if (!editor) {
    return <div className="min-h-[40vh]" style={{ background: 'var(--bg-base)' }} />;
  }

  return (
    <div className="flex flex-col h-full">
      {editable && <Toolbar editor={editor} />}
      <div className="flex-1 overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
      <style>{`
        .note-editor-content {
          color: var(--text);
          font-family: var(--font-body);
          font-size: 1rem; /* Must be >= 16px to prevent iOS Safari auto-zoom on focus */
          line-height: 1.65;
        }
        .note-editor-content h1, .note-editor-content h2, .note-editor-content h3 {
          font-family: var(--font-display);
          color: var(--text);
          letter-spacing: 0.01em;
          margin-top: 1.4em;
          margin-bottom: 0.5em;
          font-weight: 700;
        }
        .note-editor-content h1 { font-size: 1.75rem; }
        .note-editor-content h2 { font-size: 1.35rem; }
        .note-editor-content h3 { font-size: 1.1rem; }
        .note-editor-content p { margin: 0.6em 0; color: var(--text); }
        .note-editor-content a { color: var(--crimson-light); text-decoration: none; }
        .note-editor-content a:hover { text-decoration: underline; }
        .note-editor-content ul, .note-editor-content ol { padding-left: 1.4em; margin: 0.6em 0; }
        .note-editor-content li { margin: 0.2em 0; }
        .note-editor-content li::marker { color: var(--crimson); }
        .note-editor-content blockquote {
          border-left: 3px solid var(--crimson);
          padding-left: 1em;
          margin: 0.8em 0;
          color: var(--text-dim);
          font-style: italic;
        }
        .note-editor-content code {
          background: var(--bg-high);
          color: var(--crimson-light);
          padding: 0.1em 0.3em;
          border-radius: 4px;
          font-family: var(--font-mono);
          font-size: 0.9em;
        }
        .note-editor-content pre {
          background: var(--bg-deep);
          color: var(--text);
          padding: 1em;
          border-radius: 8px;
          overflow-x: auto;
          margin: 0.8em 0;
          border: 1px solid var(--border);
        }
        .note-editor-content pre code {
          background: transparent;
          color: var(--text);
          padding: 0;
        }
        .note-editor-content p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: var(--text-muted);
          float: left;
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
};

export default NoteEditor;
